// src/components/CommentThread.jsx
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import "./Comment.css";

export default function CommentThread({ findingId, onToast, currentUser, onRefresh }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const listRef = useRef(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/api/audit.php", {
        params: { comments: 1, finding_id: findingId },
        withCredentials: true,
      });
      setItems(res.data?.data || []);
    } catch {
      onToast?.("Failed to load comments", "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (findingId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [findingId]);

  const add = async () => {
    const body = text.trim();
    if (!body) return;

    const temp = {
      id: `temp-${Date.now()}`,
      finding_id: findingId,
      author: currentUser?.display_name || currentUser?.username || "You",
      body,
      created_at: new Date().toISOString(),
      pending: true,
    };

    setItems((p) => [temp, ...p]);
    setText("");
    setSaving(true);

    try {
      const res = await axios.post(
        "/api/audit.php?comments=1",
        { finding_id: findingId, body },
        { withCredentials: true }
      );
      const created = res.data?.data;
      if (!created) throw new Error("No data returned");
      setItems((p) => p.map((c) => (c.id === temp.id ? created : c)));
      onToast?.("Comment added", "success");
      onRefresh?.({ preserveExpanded: true });
    } catch {
      setItems((p) => p.filter((c) => c.id !== temp.id));
      onToast?.("Failed to add comment", "danger");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (currentUser?.user_type !== "admin") {
      onToast?.("Only admins can delete comments", "warning");
      return;
    }

    const backup = items;
    setItems((p) => p.filter((c) => c.id !== id));

    try {
      const res = await axios.delete("/api/audit.php?comments=1", {
        data: { id },
        withCredentials: true,
      });
      if (!res.data?.success) throw new Error();
      onToast?.("Comment deleted", "success");
      onRefresh?.({ preserveExpanded: true });
    } catch {
      setItems(backup);
      onToast?.("Failed to delete comment", "danger");
    }
  };

  return (
    <div className="comment-thread">
      <div className="comment-thread__header">Comments</div>

      <div className="comment-thread__list" ref={listRef}>
        {loading ? (
          <div className="comment-thread__loading">Loading…</div>
        ) : items.length === 0 ? (
          <div className="comment-thread__empty">No comments yet</div>
        ) : (
          items.map((c) => (
            <div key={c.id} className={`comment ${c.pending ? "comment--pending" : ""}`}>
              <div className="comment__meta">
                <span className="comment__author">{c.author || "Unknown"}</span>
                <span className="comment__dot">·</span>
                <time className="comment__time" dateTime={c.created_at}>
                  {new Date(c.created_at).toLocaleString()}
                </time>
                {currentUser?.user_type === "admin" && (
                  <button
                    className="comment__delete"
                    title="Delete"
                    onClick={() => remove(c.id)}
                  >
                    <i className="bi bi-trash" />
                  </button>
                )}
              </div>
              <div className="comment__body">{c.body}</div>
            </div>
          ))
        )}
      </div>

      <div className="comment-thread__composer">
        <textarea
          className="form-control"
          placeholder="Write a comment…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={saving}
          rows={2}
        />
        <button
          className="btn btn-primary"
          onClick={add}
          disabled={saving || !text.trim()}
        >
          {saving ? "Posting…" : "Post"}
        </button>
      </div>
    </div>
  );
}
